import { Autocomplete, Chip, TextField, Stack, Typography } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BatchNumberResult } from 'src/api/certificate-extraction'

interface BatchNumberEditorProps {
  value: string[]
  onChange: (value: string[]) => void
  ranges?: BatchNumberResult[]
}

const expandRange = (range: BatchNumberResult): string[] => {
  const startStr = range.range_start || ''
  const endStr = range.range_end || ''

  // Handle prefixed ranges like "250208-0001" to "250208-0384"
  const startMatch = startStr.match(/^(.*?)(\d+)$/)
  const endMatch = endStr.match(/^(.*?)(\d+)$/)

  if (!startMatch || !endMatch) return [range.raw]

  const prefix = startMatch[1]
  const endPrefix = endMatch[1]

  // If prefixes don't match, treat as simple numeric range
  const startNum = parseInt(startMatch[2])
  const endNum = parseInt(endMatch[2])
  const padLength = startMatch[2].length

  if (isNaN(startNum) || isNaN(endNum) || endNum <= startNum || endNum - startNum > 10000) {
    return [range.raw]
  }

  const result: string[] = []
  for (let i = startNum; i <= endNum; i++) {
    result.push(prefix + String(i).padStart(padLength, '0'))
  }
  return result
}

export const BatchNumberEditor: React.FC<BatchNumberEditorProps> = ({ value, onChange, ranges }) => {
  const { t } = useTranslation(['product-certification'])
  const hasAutoExpanded = useRef(false)

  // Auto-expand ranges on mount
  useEffect(() => {
    if (hasAutoExpanded.current || !ranges || ranges.length === 0) return
    hasAutoExpanded.current = true

    let newValues = [...value]
    for (const range of ranges) {
      if (!range.is_range) continue
      const expanded = expandRange(range)
      // Remove the raw range string and add expanded values
      newValues = newValues.filter(v => v !== range.raw)
      newValues.push(...expanded)
    }

    if (newValues.length !== value.length) {
      onChange(newValues)
    }
  }, [ranges])

  return (
    <Stack spacing={1}>
      <Autocomplete
        multiple
        freeSolo
        autoSelect
        value={value}
        onChange={(_, newValue) => onChange(newValue as string[])}
        options={[]}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              sx={{ blockSize: 25, fontSize: 12 }}
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              key={`${option}-${index}`}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label={t('batchNumbers', { ns: 'product-certification', defaultValue: 'Batch Numbers' })}
            placeholder={t('addBatchNumber', { ns: 'product-certification', defaultValue: 'Add batch number...' })}
            size="small"
          />
        )}
      />
      {ranges && ranges.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('rangesAutoExpanded', {
            ns: 'product-certification',
            defaultValue: 'Ranges auto-expanded into individual batch numbers',
          })}
        </Typography>
      )}
    </Stack>
  )
}
